import { getEntityValueFromARef } from '../../../types';
import { getEntityUpsertSession } from './EntityUpsertSessionContext';
import type { RenderFieldLocals } from './renderFieldLocals';
import type { createContentTypeHelpers } from './renderFieldLocals';

export type ContentTypeHelpers = ReturnType<typeof createContentTypeHelpers>;

/** Flatten session + locals + helpers so renderField body matches original dialog variable names. */
export function bindRenderFieldScope(
    locals: RenderFieldLocals,
    helpers: ContentTypeHelpers,
    attr: Record<string, unknown>
) {
    const ctx = getEntityUpsertSession();
    const {
        wrapWithFieldDebug,
        formData,
        existingEntity,
        entityType,
        entityId,
        isEditMode,
        isViewSurface,
        loading,
        schemaAttributes,
        deferredArrayRefNamesSet,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        onRequestAttributeValue,
        loadingAttribute,
        handleFieldChange,
        handleFieldChangeRef,
        isSetTransition,
        selectedActionWorkflow,
        actionWorkflowError,
        setActionWorkflowBrowserOpen,
        formKeyFromAttrName,
        handleGroupPreviewMembers,
        handleFilterPreview,
        attributeRefBrowserField,
        setAttributeRefBrowserField,
        loadingPossessiveScopeTier2,
        tier2Entitlement,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromTier2,
        possessiveScope,
        isPluginFunction,
        isRequestApprovalType,
        isPolicyRule,
        handleRequestorSetChange,
        handleTargetSetChange,
        targetSetFilterScope,
        relativeTargetAttributeError,
        setRelativeTargetAttributeError,
        policyRuleOnlySingleValueAttributesSelected,
        isContent,
        onContentLinkClick,
        isEmailTemplate,
        isFile,
        isGroup,
        originalData,
        setOriginalData,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        multiValuedRawInputs,
        setMultiValuedRawInputs,
        emailTemplatesList,
        loadingEmailTemplates,
        allEntityTypes,
        entityTypesFromContext,
        disjointedEntityTypes,
        possessiveTargetAttributeKey,
        loadingPossessiveTargetAttributeTier2,
        tier2PossessiveTargetAttributeEntitlement,
        referenceAttributesFromPossessiveTargetAttribute,
        selectedReferenceAttributeInfo,
        actorsAttributeBrowserOpen,
        setActorsAttributeBrowserOpen,
        setPossessiveWipeConfirmPending,
        setPossessiveWipeConfirmOpen,
        attributesFromPossessiveTargetAttributeTier2,
        contentTypesCacheService,
        setError,
        targetEntitlement,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        setMultiRefBrowserField,
    } = ctx;

    const {
        name,
        label,
        value,
        attrRef,
        isStubValueInner,
        isRequired,
        allowedValues,
        bindingRegex,
        isReadOnlyField,
        isFieldDisabled,
        isPossessiveType,
    } = locals;

    const { getBodyContentType, isHtmlField } = helpers;

    const managedType = (
        getEntityValueFromARef('ManagedType', formData) ?? existingEntity?.ManagedType ?? ''
    ).toString();
    const isExternalManaged =
        entityType?.toLowerCase() === 'group' && managedType?.toLowerCase() === 'external';

    return {
        attr,
        wrapWithFieldDebug,
        formData,
        existingEntity,
        entityType,
        entityId,
        isEditMode,
        isViewSurface,
        loading,
        schemaAttributes,
        deferredArrayRefNamesSet,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        onRequestAttributeValue,
        loadingAttribute,
        handleFieldChange,
        handleFieldChangeRef,
        isSetTransition,
        selectedActionWorkflow,
        actionWorkflowError,
        setActionWorkflowBrowserOpen,
        formKeyFromAttrName,
        handleGroupPreviewMembers,
        handleFilterPreview,
        attributeRefBrowserField,
        setAttributeRefBrowserField,
        loadingPossessiveScopeTier2,
        tier2Entitlement,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromTier2,
        possessiveScope,
        isPluginFunction,
        isRequestApprovalType,
        isPolicyRule,
        handleRequestorSetChange,
        handleTargetSetChange,
        targetSetFilterScope,
        relativeTargetAttributeError,
        setRelativeTargetAttributeError,
        policyRuleOnlySingleValueAttributesSelected,
        isContent,
        onContentLinkClick,
        isEmailTemplate,
        isFile,
        isGroup,
        originalData,
        setOriginalData,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        multiValuedRawInputs,
        setMultiValuedRawInputs,
        emailTemplatesList,
        loadingEmailTemplates,
        allEntityTypes,
        entityTypesFromContext,
        disjointedEntityTypes,
        possessiveTargetAttributeKey,
        loadingPossessiveTargetAttributeTier2,
        tier2PossessiveTargetAttributeEntitlement,
        referenceAttributesFromPossessiveTargetAttribute,
        selectedReferenceAttributeInfo,
        actorsAttributeBrowserOpen,
        setActorsAttributeBrowserOpen,
        setPossessiveWipeConfirmPending,
        setPossessiveWipeConfirmOpen,
        attributesFromPossessiveTargetAttributeTier2,
        contentTypesCacheService,
        setError,
        targetEntitlement,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        setMultiRefBrowserField,
        name,
        label,
        value,
        attrRef,
        isStubValueInner,
        isRequired,
        allowedValues,
        bindingRegex,
        isReadOnlyField,
        isFieldDisabled,
        isPossessiveType,
        getBodyContentType,
        isHtmlField,
        isExternalManaged,
    };
}

export type RenderFieldScope = ReturnType<typeof bindRenderFieldScope>;
